/**
 * Document type constants and utilities
 *
 * Core document types supported by the system:
 * - BOL, DR, PICTURE: Ingested from S3 paths
 * - AT, INVOICE: Classified by AI
 * - UNKNOWN: Unclassified or incorrectly classified documents
 */

import { logger } from "../utils/logger";

export const DOCUMENT_TYPES = {
  BOL: "BOL", // Bill of Lading
  DR: "DR", // Delivery Receipt / POD
  AT: "AT", // Agent Transfer
  INVOICE: "INVOICE", // Invoice
  PICTURE: "PICTURE", // Freight Photo
  CARRIER_ADDENDUM: "CARRIER_ADDENDUM", // Carrier Addendum (Linehaul)
  UNKNOWN: "UNKNOWN", // Not yet classified
} as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES];

export const DOCUMENT_TYPE_OPTIONS: Array<{ value: DocumentType; label: string }> = [
  { value: DOCUMENT_TYPES.BOL, label: "Bill of Lading (BOL)" },
  { value: DOCUMENT_TYPES.DR, label: "Delivery Receipt (DR)" },
  { value: DOCUMENT_TYPES.AT, label: "Agent Transfer (AT)" },
  { value: DOCUMENT_TYPES.INVOICE, label: "Invoice" },
  { value: DOCUMENT_TYPES.PICTURE, label: "Freight Photo" },
  { value: DOCUMENT_TYPES.CARRIER_ADDENDUM, label: "Carrier Addendum" },
  { value: DOCUMENT_TYPES.UNKNOWN, label: "Unknown / Unclassified" },
];
export function getDocumentTypeLabel(type: string): string {
  const option = DOCUMENT_TYPE_OPTIONS.find((opt) => opt.value === type);

  if (!option) {
    // Graceful fallback for legacy or invalid document types
    logger.warn("Unknown document type encountered", { document_type: type });
    return `${type} (Legacy)`;
  }

  return option.label;
}

/**
 * Type guard to check if a string is a valid DocumentType
 */
export function isValidDocumentType(type: string): type is DocumentType {
  return Object.values(DOCUMENT_TYPES).includes(type as DocumentType);
}
